import React, { useState, useEffect } from 'react';
import { MessageCircle, Plus, Minus, MapPin, Phone, Mail } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CONTACT } from '../data/products';

const PRODUCTS = [
  {
    id: 'cream',
    price: 12,
    nameEn: 'MISKY Face & Body Cream',
    nameAr: 'كريم مسكي للوجه والجسم',
    subEn: 'Ceramide Complex 2% · 150 ml',
    subAr: 'مركب السيراميد 2% · 150 مل',
    icon: '🧴',
  },
  {
    id: 'facewash',
    price: 10,
    nameEn: 'MISKY Tea Tree Face Wash',
    nameAr: 'غسول الوجه — شجرة الشاي والبابونج',
    subEn: 'Tea Tree & Chamomile · 160 ml',
    subAr: 'شجرة الشاي والبابونج · 160 مل',
    icon: '🌿',
  },
];

export default function OrderSection() {
  const { t, lang } = useLang();
  const o = t.order;
  const c = t.contact;
  const isAr = lang === 'ar';
  const currency = isAr ? 'دينار' : 'JD';

  const [quantities, setQuantities] = useState({ cream: 0, facewash: 0 });
  const [name, setName] = useState('');
  const [note, setNote] = useState('');

  const setQty = (id, val) => setQuantities(q => ({ ...q, [id]: Math.max(0, val) }));

  const grandTotal = PRODUCTS.reduce((sum, p) => sum + p.price * quantities[p.id], 0);
  const hasItems = PRODUCTS.some(p => quantities[p.id] > 0);

  // Pre-select product when clicking "Order Now" from product cards
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail;
      if (id === 'facewash' || id === 'cream') {
        setQuantities(q => ({ ...q, [id]: q[id] === 0 ? 1 : q[id] }));
      }
    };
    window.addEventListener('misky-select-product', handler);
    return () => window.removeEventListener('misky-select-product', handler);
  }, []);

  const handleOrder = () => {
    const selected = PRODUCTS.filter(p => quantities[p.id] > 0);
    let msg;

    if (isAr) {
      msg = `مرحباً Misky! 👋\n\nأود تقديم الطلب التالي:\n\n`;
      selected.forEach(p => {
        const subtotal = p.price * quantities[p.id];
        msg += `📦 *${p.nameAr}*\n`;
        msg += `   ${p.subAr}\n`;
        msg += `   الكمية: ${quantities[p.id]}\n`;
        msg += `   ${p.price} دينار × ${quantities[p.id]} = *${subtotal} دينار*\n\n`;
      });
      msg += `💰 *الإجمالي الكلي: ${grandTotal} دينار*`;
      if (name) msg += `\n\n👤 الاسم: ${name}`;
      if (note) msg += `\n📝 ملاحظة: ${note}`;
      msg += `\n\nأرجو تأكيد الطلب، شكراً! ✨`;
    } else {
      msg = `Hello Misky! 👋\n\nI'd like to order:\n\n`;
      selected.forEach(p => {
        const subtotal = p.price * quantities[p.id];
        msg += `📦 *${p.nameEn}*\n`;
        msg += `   ${p.subEn}\n`;
        msg += `   Qty: ${quantities[p.id]}\n`;
        msg += `   ${p.price} JD × ${quantities[p.id]} = *${subtotal} JD*\n\n`;
      });
      msg += `💰 *Grand Total: ${grandTotal} JD*`;
      if (name) msg += `\n\n👤 Name: ${name}`;
      if (note) msg += `\n📝 Note: ${note}`;
      msg += `\n\nPlease confirm my order. Thank you! ✨`;
    }

    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <section id="order" className="py-24 bg-gradient-to-br from-misky-silver to-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-misky-mid text-xs uppercase mb-3">{o.sectionLabel}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-misky-black">{o.title}</h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-misky-silver shadow-sm p-8">

            {/* Product rows */}
            <div className="space-y-4 mb-8">
              {PRODUCTS.map(p => {
                const qty = quantities[p.id];
                const subtotal = p.price * qty;
                return (
                  <div key={p.id} className={`rounded-2xl border-2 transition-all ${qty > 0 ? 'border-misky-black bg-misky-white' : 'border-misky-silver bg-white'}`}>
                    <div className="p-4">
                      {/* Top row: icon + name + price */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0 text-xl">
                          {p.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-misky-black text-sm leading-snug">{isAr ? p.nameAr : p.nameEn}</div>
                          <div className="text-xs text-misky-mid">{isAr ? p.subAr : p.subEn}</div>
                        </div>
                        <div className="text-sm font-bold text-misky-black flex-shrink-0">{p.price} {currency}</div>
                      </div>

                      {/* Bottom row: qty controls + subtotal */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setQty(p.id, qty - 1)}
                            className="w-8 h-8 rounded-full border border-misky-silver flex items-center justify-center hover:bg-misky-silver transition-colors">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center font-bold text-misky-black">{qty}</span>
                          <button onClick={() => setQty(p.id, qty + 1)}
                            className="w-8 h-8 rounded-full border border-misky-silver flex items-center justify-center hover:bg-misky-silver transition-colors">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        {qty > 0 && (
                          <span className="text-xs text-misky-mid">
                            {p.price} × {qty} = <span className="font-bold text-misky-black">{subtotal} {currency}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Grand total */}
            {hasItems && (
              <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-misky-black text-white mb-6">
                <span className="text-sm font-medium">{o.total}</span>
                <span className="text-xl font-bold">{grandTotal} {currency}</span>
              </div>
            )}

            {/* Name */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-misky-black mb-2">{o.nameLabel}</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={o.namePlaceholder}
                className="w-full border border-misky-silver rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-misky-black transition-colors"
              />
            </div>

            {/* Note */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-misky-black mb-2">{o.noteLabel}</label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={o.notePlaceholder}
                rows={3}
                className="w-full border border-misky-silver rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-misky-black transition-colors resize-none"
              />
            </div>

            <button
              onClick={handleOrder}
              disabled={!hasItems}
              className={`wa-pulse w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold text-base transition-all duration-200 shadow-lg ${
                hasItems
                  ? 'bg-[#25D366] hover:bg-[#1da851] text-white hover:shadow-xl hover:scale-[1.01]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              {o.whatsappBtn}
            </button>

            {!hasItems && (
              <p className="text-center text-xs text-misky-mid mt-3">
                {isAr ? 'اختر كمية لمنتج واحد على الأقل' : 'Add at least one product to order'}
              </p>
            )}
          </div>

          {/* Contact */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xl font-bold text-misky-black mb-5">{o.contactTitle}</h3>

            {[
              { icon: MessageCircle, label: c.whatsapp, value: `+${CONTACT.whatsapp}`, green: true, action: () => window.open(`https://wa.me/${CONTACT.whatsapp}`, '_blank') },
              { icon: Phone, label: c.phone, value: CONTACT.phone },
              { icon: Mail, label: c.email, value: CONTACT.email },
              { icon: MapPin, label: c.location, value: CONTACT.location },
            ].map(({ icon: Icon, label, value, green, action }) => (
              <div key={label}
                onClick={action}
                className={`flex items-center gap-3 p-4 rounded-2xl border border-misky-silver bg-white shadow-sm ${action ? 'cursor-pointer hover:shadow-md hover:border-misky-black/30 transition-all' : ''}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${green ? 'bg-[#25D366]/10' : 'bg-misky-black/5'}`}>
                  <Icon className={`w-5 h-5 ${green ? 'text-[#25D366]' : 'text-misky-gray'}`} />
                </div>
                <div>
                  <div className="text-xs text-misky-mid font-medium">{label}</div>
                  <div className="text-sm font-medium text-misky-black">{value}</div>
                </div>
              </div>
            ))}

            <div className="p-4 rounded-2xl bg-misky-black text-white">
              <p className="text-xs leading-relaxed text-misky-light">{o.noteBox}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
